// ─────────────────────────────────────────────────────────────────
// Glass-i — Unposted Purchase Bills (Stage 6P.6).
//
// Operator inbox at /erp/purchase-accounting/unposted-bills.
//
// Lists approved + matched Purchase Bills that do NOT yet have an
// active accounting posting row, so an accounts operator can preview
// and post each bill to the books one click at a time.
//
// Data:
//   • purchaseAccountingApi.listUnpostedBills({ search, supplier_id,
//       supplier_invoice_no, bill_date_from, bill_date_to, bill_no,
//       limit, offset })
//     The backend (purchaseAccountingReports.service.listUnposted-
//     PurchaseBills) is authoritative for the eligibility filter:
//       (a) pb.status IN ('APPROVED', 'POSTING_PENDING')
//       (b) pb.match_status = 'MATCHED'
//       (c) NO active posting row for this bill
//     and emits camelCase rows already (purchaseBillId, billNo,
//     billDate, supplierInvoiceNo, supplierInvoiceDate, supplierId,
//     supplierName, supplierGstin, matchStatus, status, grandTotal,
//     taxableAmount, totalTax, cgstAmount, sgstAmount, igstAmount,
//     accountingStatus: 'NOT_POSTED').
//
// Columns:
//   Bill No · Bill Date · Supplier Invoice No · Supplier
//   Match Status · Grand Total · Status · Accounting Status (always
//   "NOT_POSTED") · Actions.
//
// Per-row actions (gated by RBAC):
//   • Preview Posting    → opens a right-side Drawer that calls
//                          purchaseAccountingApi.previewPosting(billId)
//                          and renders the candidate accounting lines
//                          + the debit / credit footer + any backend
//                          blockers (e.g. unbalanced, already posted,
//                          ledger missing). Uses the shared frontend
//                          helpers in ./purchaseAccountingHelpers.
//   • Post to Accounts   → navigates to
//                          /erp/purchase-accounting/post/:billId
//                          (the dedicated posting page owns the actual
//                          POST + voucher creation; this list page
//                          never mutates).
//
// Posting math, ledger resolution, duplicate-block decisions, and
// ITC eligibility remain owned by the backend (purchase-
// AccountingPosting.service.js / purchaseAccountingLedgerResolver.js
// — both of which in turn call voucherPosting.service.createAndPost-
// Voucher). This page is a pure operator surface.
//
// Layout style: reuses the GRN list shell CSS via the .grn-shell
// wrapper class so the page matches the existing 22 px row density,
// slate-50 header strip, and sticky toolbar that the rest of
// /erp/purchase already ships. NO new CSS file.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Descriptions, Drawer, Dropdown,
  Input, Space, Spin, Statistic, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined,
  FileSearchOutlined, CheckCircleOutlined,
  AuditOutlined
} from '@ant-design/icons';

import { purchaseAccountingApi } from './purchaseAccountingApi';
import {
  normalizePurchaseAccountingPosting,
  normalizeAccountingPreviewLine,
  calculateDebitCreditTotals,
  summarizePostingPreview,
  accountingStatusLabel,
  accountingStatusColor
} from './purchaseAccountingHelpers';
import { shouldFetchStatusFor } from './unpostedBillsGuard.js';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const ACCOUNTING_STATUS_NOT_POSTED = 'NOT_POSTED';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping mirrors the rest of the control plane (en-IN). Falsy /
// non-finite inputs collapse to an em-dash so empty rows don't read
// as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// The backend list rows already arrive camelCased (see
// purchaseAccountingReports.service.listUnpostedPurchaseBills), but
// we tolerate snake on the off-chance a caller (test, fixture, replay)
// hands us the raw row shape.
function readBillId(row) {
  return (
    pickField(row, 'purchase_bill_id', 'purchaseBillId')
    || pickField(row, 'id', 'id')
    || ''
  );
}

// UUID guard for any side-effect API call that takes a bill id is
// exported from ./unpostedBillsGuard.js (a tiny pure module the
// node:test harness can import without parsing this file's JSX).
//
// Bug class pinned: with an empty list, a stale / undefined id was
// round-tripping through the backend's `assertUuid('id', ...)` and
// producing the "id must be a valid UUID." toast on page mount.
function readBillNo(row) {
  return pickField(row, 'bill_no', 'billNo') || '';
}
function readBillDate(row) {
  return pickField(row, 'bill_date', 'billDate') || null;
}
function readSupplierInvoiceNo(row) {
  return pickField(row, 'supplier_invoice_no', 'supplierInvoiceNo') || '';
}
function readSupplierName(row) {
  return pickField(row, 'supplier_name', 'supplierName') || '';
}
function readSupplierGstin(row) {
  return pickField(row, 'supplier_gstin', 'supplierGstin') || '';
}
function readMatchStatus(row) {
  return pickField(row, 'match_status', 'matchStatus') || '';
}
function readBillStatus(row) {
  return pickField(row, 'status', 'status') || '';
}
function readGrandTotal(row) {
  return pickField(row, 'grand_total', 'grandTotal');
}
function readAccountingStatus(row) {
  return (
    pickField(row, 'accounting_status', 'accountingStatus')
    || ACCOUNTING_STATUS_NOT_POSTED
  );
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function UnpostedPurchaseBillsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.purchase.accounting.*` strings cover admin / manager / staff
  // / viewer per the Stage 6P.6 permissions registry (see
  // backend/src/routes/erpPurchaseAccounting.js).
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView    = hasPerm('erp.purchase.accounting.view');
  const canPreview = hasPerm('erp.purchase.accounting.preview');
  const canPost    = hasPerm('erp.purchase.accounting.post');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [dateRange,   setDateRange]   = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Preview drawer state ─────────────────────────────────────
  const [previewOpen,    setPreviewOpen]    = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRow,     setPreviewRow]     = useState(null);
  const [previewData,    setPreviewData]    = useState(null);
  const [previewError,   setPreviewError]   = useState(null);

  // ── Debounced search wiring ──────────────────────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  // The backend list endpoint speaks limit / offset; we translate
  // page + pageSize on the way out and tolerate either rows[] or
  // a bare array on the way in.
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {
        limit:  pageSize,
        offset: (page - 1) * pageSize
      };
      const needle = (search || '').trim();
      if (needle) params.search = needle;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.bill_date_from = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.bill_date_to = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await purchaseAccountingApi.listUnpostedBills(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out)
        ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load unposted purchase bills.'),
        key: 'pa-unposted-bills'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onDates  = (v) => { setDateRange(v || null); setPage(1); };
  const onReset  = () => {
    setSearchInput('');
    setSearch('');
    setDateRange(null);
    setPage(1);
  };
  const refresh  = useCallback(() => load(), [load]);

  // ── Preview drawer handlers ──────────────────────────────────
  const openPreview = useCallback(async (row) => {
    const id = readBillId(row);
    // Guard the side-effect API call so we never round-trip a stale
    // "undefined" / empty / literal `:id` through the backend's UUID
    // validator (which 400s with "id must be a valid UUID.").
    if (!shouldFetchStatusFor(id)) {
      message.error('Could not resolve bill id for preview.');
      return;
    }
    setPreviewRow(row);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setPreviewError(null);
    setBusyId(id);
    try {
      const raw = await purchaseAccountingApi.previewPosting(id);
      // Normalise the preview header through the posting normaliser
      // (so the totals + supplier identity columns are widened to
      // numbers) and pass the candidate lines through the line
      // normaliser so the footer + balance check use the same shapes
      // as the rest of the Stage 6P.6 UI.
      const header = raw && raw.bill
        ? normalizePurchaseAccountingPosting(raw.bill)
        : null;
      const lines = Array.isArray(raw?.lines)
        ? raw.lines.map(normalizeAccountingPreviewLine).filter(Boolean)
        : [];
      setPreviewData({
        header,
        lines,
        totals:          raw?.totals          || calculateDebitCreditTotals(lines),
        blockers:        Array.isArray(raw?.blockers) ? raw.blockers : [],
        canPost:         Boolean(raw?.canPost),
        existingPosting: raw?.existingPosting || null,
        resolvedLedgers: raw?.resolvedLedgers || null
      });
    } catch (e) {
      setPreviewError(errMessage(e, 'Failed to load posting preview.'));
    } finally {
      setPreviewLoading(false);
      setBusyId(null);
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewRow(null);
    setPreviewData(null);
    setPreviewError(null);
  }, []);

  // ── Post handler — navigates to the dedicated posting page ───
  // The posting page is the only surface that calls the POST API;
  // this list page never mutates rows itself.
  const goToPost = useCallback((row) => {
    const id = readBillId(row);
    // Reject anything that wouldn't survive the backend UUID validator
    // — protects against ":id" placeholders or stale "undefined" ids
    // landing in the URL bar (which would then trigger the 400 toast
    // on the destination page).
    if (!shouldFetchStatusFor(id)) return;
    navigate(`/erp/purchase-accounting/post/${encodeURIComponent(id)}`);
  }, [navigate]);

  // ── Row-action menu ──────────────────────────────────────────
  const buildRowMenu = useCallback((_row) => {
    const items = [];
    if (canPreview) {
      items.push({
        key:   'preview',
        icon:  <FileSearchOutlined />,
        label: 'Preview Posting'
      });
    }
    if (canPost) {
      if (items.length > 0) items.push({ type: 'divider' });
      items.push({
        key:   'post',
        icon:  <CheckCircleOutlined />,
        label: 'Post to Accounts'
      });
    }
    if (items.length === 0) {
      items.push({
        key:      'noop',
        label:    'No actions available',
        disabled: true
      });
    }
    return items;
  }, [canPreview, canPost]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'preview': return openPreview(row);
      case 'post':    return goToPost(row);
      default:        return undefined;
    }
  }, [openPreview, goToPost]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Bill No',
      dataIndex: 'billNo',
      width:     170,
      render: (_v, row) => (
        <span className="grn-mono" style={{ fontWeight: 500 }}>
          {readBillNo(row) || '—'}
        </span>
      )
    },
    {
      title:     'Bill Date',
      dataIndex: 'billDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">{fmtDate(readBillDate(row))}</span>
      )
    },
    {
      title:     'Supplier Invoice No',
      dataIndex: 'supplierInvoiceNo',
      width:     180,
      render: (_v, row) => {
        const v = readSupplierInvoiceNo(row);
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = readSupplierName(row) || '—';
        const gstin = readSupplierGstin(row);
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Match Status',
      dataIndex: 'matchStatus',
      width:     130,
      render: (_v, row) => {
        const v = readMatchStatus(row);
        if (!v) return <span className="grn-muted">—</span>;
        const key = String(v).trim().toUpperCase();
        const color = key === 'MATCHED'
          ? 'success'
          : key === 'MISMATCH'
            ? 'error'
            : key === 'OVERRIDE_REQUIRED'
              ? 'warning'
              : 'default';
        return (
          <Tag color={color} className="grn-status-tag">
            {key.replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Grand Total',
      dataIndex: 'grandTotal',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">{fmtMoney(readGrandTotal(row))}</span>
      )
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     130,
      render: (_v, row) => {
        const v = readBillStatus(row);
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag">
            {String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Accounting Status',
      dataIndex: 'accountingStatus',
      width:     160,
      render: (_v, row) => {
        // Every row in this inbox is by definition NOT_POSTED, but
        // we still resolve the label + colour via the shared helper
        // so the column matches the other Stage 6P.6 grids visually.
        const v = readAccountingStatus(row);
        return (
          <Tag
            color={accountingStatusColor(v)}
            className="grn-status-tag"
          >
            {accountingStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id    = readBillId(row);
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) {
                  domEvent.stopPropagation();
                }
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view purchase accounting."
          description={
            <>
              Ask your master account to grant{' '}
              <code>erp.purchase.accounting.view</code>.
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><AuditOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Unposted Purchase Bills</span>
            <span className="grn-headstrip__subtitle">
              Approved, matched purchase bills that have not yet been
              posted to the accounting ledger. Preview the proposed
              voucher lines or open the posting page to push to the
              books.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> awaiting posting
          </span>
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={refresh}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Toolbar */}
      <div className="grn-toolbar">
        <Space size={6} wrap>
          <Input
            allowClear
            size="small"
            placeholder="Search bill no, supplier, supplier invoice no"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 320 }}
          />
          <RangePicker
            size="small"
            allowEmpty={[true, true]}
            value={dateRange}
            onChange={onDates}
            format="DD-MMM-YYYY"
            placeholder={['Bill date from', 'Bill date to']}
          />
          <Button size="small" onClick={onReset}>
            Reset
          </Button>
        </Space>
      </div>

      {/* Grid */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) => readBillId(r) || readBillNo(r)}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => {
                if (canPreview) openPreview(row);
              }
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No approved, matched purchase bills are waiting to be posted.'
            }}
          />
        </Spin>
      </Card>

      {/* Preview drawer */}
      <Drawer
        title={
          previewRow
            ? `Posting preview — Bill ${readBillNo(previewRow) || ''}`.trim()
            : 'Posting preview'
        }
        placement="right"
        width={820}
        open={previewOpen}
        onClose={closePreview}
        destroyOnClose
        extra={
          previewRow && canPost
            ? (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  const row = previewRow;
                  closePreview();
                  goToPost(row);
                }}
                disabled={
                  !previewData
                  || previewData.canPost === false
                }
              >
                Post to Accounts
              </Button>
            )
            : null
        }
      >
        {previewLoading ? (
          <Spin spinning>
            <div style={{ minHeight: 200 }} />
          </Spin>
        ) : previewError ? (
          <Alert type="error" showIcon message={previewError} />
        ) : previewData ? (
          <PreviewBody data={previewData} />
        ) : (
          <Alert type="info" showIcon message="No preview data available." />
        )}
      </Drawer>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Preview drawer body — renders the candidate voucher lines, a
// debit / credit summary card, the resolved supplier identity, and
// any backend blockers (e.g. unbalanced voucher, already posted,
// ledger missing). Pure presentation; never mutates the incoming
// data shape.
// ─────────────────────────────────────────────────────────────────

function PreviewBody({ data }) {
  const summary = summarizePostingPreview({
    lines:    Array.isArray(data.lines) ? data.lines : [],
    blockers: Array.isArray(data.blockers) ? data.blockers : []
  });
  const totals = data.totals && typeof data.totals === 'object'
    ? data.totals
    : calculateDebitCreditTotals(data.lines);
  const header   = data.header || {};
  const blockers = Array.isArray(data.blockers) ? data.blockers : [];

  const lineColumns = [
    {
      title:     '#',
      dataIndex: 'lineNo',
      width:     48,
      align:     'right',
      render: (_v, _row, idx) => (
        <span className="grn-mono">{_v || idx + 1}</span>
      )
    },
    {
      title:     'Type',
      dataIndex: 'lineType',
      width:     150,
      render: (v) => (
        v
          ? (
            <Tag className="grn-status-tag">
              {String(v).replace(/_/g, ' ')}
            </Tag>
          )
          : <span className="grn-muted">—</span>
      )
    },
    {
      title:     'Ledger',
      dataIndex: 'ledgerAccountCode',
      render: (_v, c) => {
        const code = c.ledgerAccountCode;
        const name = c.ledgerAccountName;
        if (!code && !name) return <span className="grn-muted">—</span>;
        return (
          <Space size={4} direction="vertical" style={{ lineHeight: 1.2 }}>
            <span className="grn-mono" style={{ fontSize: 12 }}>
              {code || '—'}
            </span>
            {name
              ? (
                <span style={{ fontSize: 11.5, color: '#475569' }}>
                  {name}
                </span>
              )
              : null}
          </Space>
        );
      }
    },
    {
      title:     'Debit',
      dataIndex: 'debitAmount',
      width:     130,
      align:     'right',
      render: (v) => {
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{fmtMoney(v)}</span>;
      }
    },
    {
      title:     'Credit',
      dataIndex: 'creditAmount',
      width:     130,
      align:     'right',
      render: (v) => {
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{fmtMoney(v)}</span>;
      }
    }
  ];

  return (
    <div>
      {/* Summary statistics card */}
      <Card size="small" style={{ marginBottom: 12 }} bordered>
        <Space size={32} wrap>
          <Statistic title="Lines" value={summary.lineCount} />
          <Statistic
            title="Total Debit"
            value={fmtMoney(totals.totalDebit)}
            valueStyle={{ color: '#0f766e' }}
          />
          <Statistic
            title="Total Credit"
            value={fmtMoney(totals.totalCredit)}
            valueStyle={{ color: '#0f766e' }}
          />
          <Statistic
            title="Difference"
            value={fmtMoney(totals.difference)}
            valueStyle={{
              color: summary.balanced ? '#16a34a' : '#dc2626'
            }}
          />
          <Statistic
            title="Blockers"
            value={summary.blockerCount}
            valueStyle={{
              color: summary.blockerCount > 0 ? '#dc2626' : '#64748b'
            }}
          />
        </Space>
      </Card>

      {/* Bill header — supplier identity + bill totals. */}
      {header && (header.billNo || header.supplierName) ? (
        <Card
          size="small"
          title="Source Bill"
          style={{ marginBottom: 12 }}
          bordered
        >
          <Descriptions
            size="small"
            column={2}
            colon
            labelStyle={{ fontSize: 12, color: '#64748b' }}
            contentStyle={{ fontSize: 12.5 }}
          >
            {header.billNo ? (
              <Descriptions.Item label="Bill No">
                {header.billNo}
              </Descriptions.Item>
            ) : null}
            {header.billDate ? (
              <Descriptions.Item label="Bill Date">
                {fmtDate(header.billDate)}
              </Descriptions.Item>
            ) : null}
            {header.supplierInvoiceNo ? (
              <Descriptions.Item label="Supplier Invoice No">
                {header.supplierInvoiceNo}
              </Descriptions.Item>
            ) : null}
            {header.supplierInvoiceDate ? (
              <Descriptions.Item label="Supplier Invoice Date">
                {fmtDate(header.supplierInvoiceDate)}
              </Descriptions.Item>
            ) : null}
            {header.supplierName ? (
              <Descriptions.Item label="Supplier">
                {header.supplierName}
              </Descriptions.Item>
            ) : null}
            {header.supplierGstin ? (
              <Descriptions.Item label="GSTIN">
                {header.supplierGstin}
              </Descriptions.Item>
            ) : null}
            {header.taxableAmount !== undefined ? (
              <Descriptions.Item label="Taxable">
                {fmtMoney(header.taxableAmount)}
              </Descriptions.Item>
            ) : null}
            {header.grandTotal !== undefined ? (
              <Descriptions.Item label="Grand Total">
                {fmtMoney(header.grandTotal)}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </Card>
      ) : null}

      {/* Blocker list (if any). Each blocker is shown as a single
          Alert so the operator can read the code + message side by
          side. The backend is the source of truth for blocker
          decisions; we surface them verbatim. */}
      {blockers.length > 0 ? (
        <div style={{ marginBottom: 12 }}>
          {blockers.map((b, idx) => {
            const code = (b && typeof b === 'object' && b.code)
              ? String(b.code)
              : null;
            const msg = (b && typeof b === 'object' && b.message)
              ? String(b.message)
              : (typeof b === 'string' ? b : 'Posting blocker.');
            return (
              <Alert
                key={`pa-blocker-${idx}`}
                type="error"
                showIcon
                style={{ marginBottom: 6 }}
                message={code ? `${code}` : 'Posting blocker'}
                description={msg}
              />
            );
          })}
        </div>
      ) : null}

      {/* Voucher lines */}
      <Table
        size="small"
        rowKey={(line, idx) =>
          line.lineNo !== undefined && line.lineNo !== null
            ? `line-${line.lineNo}-${idx}`
            : `line-${idx}`}
        dataSource={Array.isArray(data.lines) ? data.lines : []}
        columns={lineColumns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No voucher lines were generated.' }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3} align="right">
                <strong style={{ fontSize: 12 }}>Totals</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <strong className="grn-mono">{fmtMoney(totals.totalDebit)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                <strong className="grn-mono">{fmtMoney(totals.totalCredit)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

      {!summary.balanced ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="Voucher is unbalanced"
          description={`Debit ${fmtMoney(totals.totalDebit)} vs Credit ${fmtMoney(totals.totalCredit)} — difference ${fmtMoney(totals.difference)}. The backend will block posting until this resolves.`}
        />
      ) : null}

      {data.existingPosting ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="Existing posting on file"
          description={
            <span>
              Posting <strong>{data.existingPosting.postingNo || data.existingPosting.id}</strong>
              {' '}is currently in status{' '}
              <strong>{accountingStatusLabel(data.existingPosting.postingStatus) || data.existingPosting.postingStatus}</strong>.
            </span>
          }
        />
      ) : null}
    </div>
  );
}
